/** Procurement (R-234) — hooks over gateway-local's `/v1/procurement/*`.
 *
 * ⚠ These are SITE-LOCAL routes, and that is deliberate: a farm must be able to
 * raise, approve, order and receive with no uplink. They still work from the
 * cloud console unchanged — `resolvePath` rewrites `/v1/*` to
 * `/sites/{siteId}/v1/*` (R-154 drill-down), so there is ONE implementation for
 * both tiers rather than a cloud copy that drifts.
 *
 * RBAC is three modules, not one, because the separation of duties is the point:
 *   procurement  read list · write raise/edit own · approve decide · admin rules
 *   purchasing   read orders · write issue/cancel a PO
 *   receiving    write books the goods in
 * `close` is the one hand-written gate: purchasing:write OR receiving:write.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../core/client';
import { useSiteScope } from '../../core/site-scope';
import type {
  ApprovalRule,
  ApprovalRuleBody,
  ApproverOption,
  DecideBody,
  DeleteRuleResult,
  DiscardResult,
  IssueOrderBody,
  MyApproval,
  NewLineBody,
  NewRequestBody,
  OrdersQuery,
  PatchRequestBody,
  ProcurementReadiness,
  PurchaseOrder,
  PurchaseOrderDetail,
  PurchaseRequest,
  PurchaseRequestDetail,
  PurchaseRequestLine,
  RecordType,
  RequesterOption,
  RequestsQuery,
  SimulateBody,
  SimulateResult,
  SubmitResult,
} from './procurement.types';

const BASE = '/v1/procurement';
const KEY = ['procurement'] as const;

/** Orders live under the same scope but a different RBAC module, so they get
 * their own cache key — a buyer without `procurement: read` still caches them. */
const ORDERS_KEY = [...KEY, 'orders'] as const;

function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

// ─── readiness ───────────────────────────────────────────────────────────────

/** What is still missing before a request can be raised (a location, a class, a
 * model, an approval rule). Drives the empty state; `createRequest` refuses with
 * the SAME list, so the screen and the error can never disagree. */
export function useProcurementReadiness() {
  const { scope, ready } = useSiteScope();
  return useQuery({
    queryKey: [...KEY, scope, 'readiness'],
    queryFn: () => api.get<ProcurementReadiness>(`${BASE}/readiness`),
    enabled: ready,
    staleTime: 60_000,
  });
}

// ─── approval rules (admin) ──────────────────────────────────────────────────

/** ⚠ With NO rule, every request is silently auto-approved. That is why the
 * prototype treats this screen as a prerequisite, not a setting. */
export function useApprovalRules(recordType?: RecordType) {
  const { scope, ready } = useSiteScope();
  return useQuery({
    enabled: ready,
    queryKey: [...KEY, scope, 'rules', { recordType }],
    queryFn: () => api.get<ApprovalRule[]>(`${BASE}/rules${qs({ record_type: recordType })}`),
  });
}

/** Only people who could action the rows this rule would create. */
export function useApproverOptions(recordType: RecordType = 'procurement') {
  const { scope, ready } = useSiteScope();
  return useQuery({
    enabled: ready,
    queryKey: [...KEY, scope, 'rules', 'approvers', { recordType }],
    queryFn: () => api.get<ApproverOption[]>(`${BASE}/rules/approvers${qs({ record_type: recordType })}`),
    staleTime: 300_000,
  });
}

/** Who a request may be raised FOR. The server validates `requested_for` against
 * this exact set, so the picker cannot offer a name submit would refuse. */
export function useRequesterOptions() {
  const { scope, ready } = useSiteScope();
  return useQuery({
    enabled: ready,
    queryKey: [...KEY, scope, 'requesters'],
    queryFn: () => api.get<RequesterOption[]>(`${BASE}/requesters`),
    staleTime: 300_000,
  });
}

/** Run the matcher against a hypothetical record. Creates nothing — this is the
 * rule screen's "who would sign this?" answer. */
export function useSimulateChain() {
  return useMutation({
    mutationFn: (body: SimulateBody) => api.post<SimulateResult>(`${BASE}/rules/simulate`, body),
  });
}

export function useCreateApprovalRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ApprovalRuleBody) => api.post<ApprovalRule>(`${BASE}/rules`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** ⚠ POST, not PATCH — the backend registers `#[post("/rules/{id}")]`. */
export function useUpdateApprovalRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: ApprovalRuleBody & { id: string }) =>
      api.post<ApprovalRule>(`${BASE}/rules/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Approval rows already built from this rule SURVIVE its deletion — an in-flight
 * chain is never re-shaped by editing the rules behind it. `approvals_kept` says
 * how many, so the screen can tell the admin what they did not change. */
export function useDeleteApprovalRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<DeleteRuleResult>(`${BASE}/rules/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

// ─── requests ────────────────────────────────────────────────────────────────

export function usePurchaseRequests(query: RequestsQuery = {}) {
  const { scope, ready } = useSiteScope();
  return useQuery({
    enabled: ready,
    queryKey: [...KEY, scope, 'requests', query],
    queryFn: () =>
      api.get<PurchaseRequest[]>(
        `${BASE}/requests${qs({ state: query.state, mine: query.mine, q: query.q })}`,
      ),
    // A filter change keeps the current rows on screen (dimmed by `QueryTable`)
    // instead of blanking the table for every keystroke of the search.
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });
}

/** Header + lines + chain + live preview in ONE response. */
export function usePurchaseRequest(id: string | null | undefined) {
  const { scope, ready } = useSiteScope();
  return useQuery({
    queryKey: [...KEY, scope, 'requests', 'detail', id],
    queryFn: () => api.get<PurchaseRequestDetail>(`${BASE}/requests/${id}`),
    enabled: ready && !!id,
  });
}

/** Raises the draft there and then — there is no separate new-request page and no
 * save-draft step. An untouched draft is pruned when you navigate away, so
 * backing out leaves nothing behind. */
export function useCreateRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NewRequestBody) => api.post<PurchaseRequest>(`${BASE}/requests`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Draft only, own only (admin bypasses). ⚠ `clear_need_by` is how you REMOVE the
 * date — omitting `need_by` leaves it untouched. */
export function useUpdateRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: PatchRequestBody & { id: string }) =>
      api.post<PurchaseRequest>(`${BASE}/requests/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Prune a blank draft (no site, no justification, no need-by, no lines). Returns
 * `discarded: false` when the draft had content and was left alone. */
export function useDiscardBlankDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<DiscardResult>(`${BASE}/requests/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useAddRequestLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: NewLineBody & { id: string }) =>
      api.post<PurchaseRequestLine>(`${BASE}/requests/${id}/lines`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** ⚠ Keyed on the LINE id, not the request — `/v1/procurement/lines/{id}`. */
export function useDeleteRequestLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lineId: string) => api.delete<boolean>(`${BASE}/lines/${lineId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Builds the approval chain AND flips the state in one transaction. ⚠ Check
 * `auto_approved` on the result: TRUE means no rule matched and the request was
 * approved with nobody in the loop — the console must say so out loud. */
export function useSubmitRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<SubmitResult>(`${BASE}/requests/${id}/submit`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** `procurement: approve`. One rejection ends the whole request.
 *
 * Returns the REQUEST, not the approval row — deciding may advance the chain to
 * the next level or settle the whole record, so the caller needs the header's new
 * state rather than the single row it just wrote. */
export function useDecideRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: DecideBody & { id: string }) =>
      api.post<PurchaseRequest>(`${BASE}/requests/${id}/decide`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Also marks every waiting/pending approval `not_required` so it stops blocking
 * its approver's queue. */
export function useCancelRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post<PurchaseRequest>(`${BASE}/requests/${id}/cancel`, { reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** The buyer's or the storekeeper's call — purchasing:write OR receiving:write. */
export function useCloseRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<PurchaseRequest>(`${BASE}/requests/${id}/close`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** How a rejected request is tried again: copied to a fresh draft. The original
 * stays closed — a rejected request is dead, not reopened. */
export function useCloneRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, client_ref }: { id: string; client_ref: string }) =>
      api.post<PurchaseRequest>(`${BASE}/requests/${id}/clone`, { client_ref }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

// ─── my approvals ────────────────────────────────────────────────────────────

/** Everything waiting on the caller, both record types. ⚠ `request` is null on
 * `change` rows — render them, do not blank them. */
export function useMyApprovals() {
  const { scope, ready } = useSiteScope();
  return useQuery({
    enabled: ready,
    queryKey: [...KEY, scope, 'approvals', 'mine'],
    queryFn: () => api.get<MyApproval[]>(`${BASE}/approvals/mine`),
    refetchInterval: 30_000,
  });
}

// ─── purchase orders ─────────────────────────────────────────────────────────

/** `enabled: false` for a screen that only WANTS the list when its seat may read
 * it (Receiving resolves request ids through it) — a disabled query never
 * fires the 403 a storekeeper without `purchasing: read` would get. */
export function usePurchaseOrders(query: OrdersQuery = {}, opts: { enabled?: boolean } = {}) {
  return useQuery({
    enabled: opts.enabled ?? true,
    queryKey: [...ORDERS_KEY, query],
    queryFn: () =>
      api.get<PurchaseOrder[]>(
        `${BASE}/orders${qs({ state: query.state, request_id: query.request_id, q: query.q })}`,
      ),
    // A filter change keeps the current rows on screen (dimmed by `QueryTable`).
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });
}

export function usePurchaseOrder(id: string | null | undefined) {
  return useQuery({
    queryKey: [...ORDERS_KEY, 'detail', id],
    queryFn: () => api.get<PurchaseOrderDetail>(`${BASE}/orders/${id}`),
    enabled: !!id,
  });
}

/** Issue a PO against an APPROVED request. ⚠ A line with `qty: 0` is left out
 * entirely, and over-ordering is refused — but a request stays orderable while it
 * still has unordered quantity, so a partial order is normal and a second PO
 * picks up the remainder. */
export function useIssueOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: IssueOrderBody & { id: string }) =>
      api.post<PurchaseOrderDetail>(`${BASE}/requests/${id}/orders`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useCancelPurchaseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post<PurchaseOrder>(`${BASE}/orders/${id}/cancel`, { reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
